/** 썸네일 추출 타임아웃 (10초) */
const THUMBNAIL_TIMEOUT_MS = 10_000;

/**
 * HTML5 video + canvas 방식으로 썸네일 추출
 */
const extractThumbnail = async (
  file: File,
  seekPercentage: number = 0.5,
): Promise<Blob | null> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      resolve(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      cleanup();
      resolve(null);
    }, THUMBNAIL_TIMEOUT_MS);

    const cleanup = () => {
      clearTimeout(timeoutId);
      URL.revokeObjectURL(video.src);
      video.src = '';
      video.load();
    };

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = () => {
      const targetTime =
        video.duration * Math.max(0, Math.min(1, seekPercentage));
      video.currentTime = Math.max(0, targetTime);
    };

    video.onseeked = () => {
      try {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            cleanup();
            resolve(blob);
          },
          'image/jpeg',
          0.8,
        );
      } catch {
        cleanup();
        resolve(null);
      }
    };

    video.onerror = () => {
      cleanup();
      resolve(null);
    };

    video.src = URL.createObjectURL(file);
  });
};

/**
 * 영상에서 썸네일을 추출하는 함수
 *
 * HTML5 video + canvas를 사용하여 지정된 위치의 프레임을 캡처합니다.
 *
 * @param file - 영상 파일
 * @param _duration - 영상 길이 (초) - 기존 API 호환성을 위해 유지 (미사용)
 * @param seekPercentage - 썸네일을 추출할 위치 (0-1 사이의 값, 기본값: 0.5 = 50%)
 * @returns Promise<Blob | null> - 썸네일 이미지 blob
 */
export const extractThumbnailFromVideo = async (
  file: File,
  _duration: number,
  seekPercentage: number = 0.5,
): Promise<Blob | null> => {
  return extractThumbnail(file, seekPercentage);
};

/**
 * 영상에서 여러 지점의 썸네일을 추출하는 함수
 *
 * @param file - 영상 파일
 * @param _duration - 영상 길이 (초) - 기존 API 호환성을 위해 유지 (미사용)
 * @param seekPercentages - 썸네일을 추출할 위치들 (0-1 사이의 값 배열)
 * @returns Promise<(Blob | null)[]> - 썸네일 이미지 blob 배열
 */
export const extractMultipleThumbnailsFromVideo = async (
  file: File,
  _duration: number,
  seekPercentages: number[] = [0.25, 0.5, 0.75],
): Promise<(Blob | null)[]> => {
  const results: (Blob | null)[] = [];
  for (const percentage of seekPercentages) {
    const thumbnail = await extractThumbnail(file, percentage);
    results.push(thumbnail);
  }
  return results;
};

/**
 * URL에서 썸네일 이미지를 fetch하여 Blob으로 변환하는 함수
 *
 * image-proxy를 통해 CORS 문제를 우회하여 외부 이미지를 가져옵니다.
 * YouTube 썸네일 URL 등 외부 이미지 URL을 처리할 때 사용합니다.
 *
 * @param thumbnailUrl - 썸네일 이미지 URL (예: https://i.ytimg.com/vi/xxx/maxresdefault.jpg)
 * @returns Promise<Blob | null> - 썸네일 이미지 blob, 실패 시 null
 */
export const fetchThumbnailFromUrl = async (
  thumbnailUrl: string,
): Promise<Blob | null> => {
  try {
    const proxyUrl = `/api/image-proxy?url=${encodeURIComponent(thumbnailUrl)}`;
    const response = await fetch(proxyUrl);

    if (!response.ok) {
      console.warn(
        `[썸네일] 이미지 fetch 실패: ${response.status} ${response.statusText}`,
      );
      return null;
    }

    return response.blob();
  } catch (error) {
    console.error('[썸네일] URL에서 이미지 fetch 실패:', error);
    return null;
  }
};
